/**
 * VideoFilterPipeline — Canvas-based video filter that processes camera frames
 * and produces a filtered MediaStream via captureStream().
 * 
 * Supports:
 * 1. CSS-like color filters (brightness, contrast, sepia, etc.)
 * 2. Background blur (via MediaPipe segmentation)
 * 3. Custom background replacement (gradients, solid colors, user images)
 *
 * The filtered stream replaces the raw camera track in the RTCPeerConnection,
 * so the remote user sees the filter effect too.
 */
import { SegmentationEngine } from './SegmentationEngine';

export const VIDEO_FILTERS = [
  { id: 'normal', label: 'Normal', filter: 'none', icon: '○', tier: 'free' },
  { id: 'brighten', label: 'Brighten', filter: 'brightness(1.3) contrast(1.05)', icon: '☀', tier: 'free' },
  { id: 'soften', label: 'Soften', filter: 'blur(0.4px) brightness(1.08) saturate(0.9)', icon: '◎', tier: 'free' },
  { id: 'warm', label: 'Warm', filter: 'sepia(0.25) saturate(1.4) brightness(1.05)', icon: '◉', tier: 'free' },
  { id: 'cool', label: 'Cool', filter: 'saturate(0.8) hue-rotate(15deg) brightness(1.05)', icon: '◈', tier: 'free' },
  { id: 'contrast', label: 'Hi-Contrast', filter: 'contrast(1.5) saturate(1.2)', icon: '◆', tier: 'free' },
  { id: 'bw', label: 'B&W', filter: 'grayscale(1) contrast(1.15)', icon: '◐', tier: 'free' },
  { id: 'cinematic', label: 'Cinematic', filter: 'contrast(1.2) saturate(1.3) brightness(0.92) sepia(0.08)', icon: '◧', tier: 'free' },
];

export class VideoFilterPipeline {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
    // Second canvas for compositing background effects
    this.bgCanvas = document.createElement('canvas');
    this.bgCtx = this.bgCanvas.getContext('2d');

    this.sourceVideo = document.createElement('video');
    this.sourceVideo.setAttribute('playsinline', '');
    this.sourceVideo.setAttribute('autoplay', '');
    this.sourceVideo.muted = true;

    this.currentFilter = 'none';
    this.running = false;
    this.filteredStream = null;
    this.rafId = null;
    this._onVideoReady = null;

    // Background effect state
    this.backgroundMode = null; // null | 'blur' | 'blur-heavy' | 'gradient' | 'solid' | 'image'
    this.backgroundValue = null; // gradient string, color, or Image object
    this.segEngine = null;
    this.segReady = false;
    this.frameCount = 0;
    this.lastMaskData = null;
    this._bgImage = null;
  }

  /**
   * Start the filter pipeline with a raw camera stream.
   * Returns a filtered MediaStream.
   */
  start(rawVideoStream) {
    return new Promise((resolve) => {
      const videoTrack = rawVideoStream.getVideoTracks()[0];
      if (!videoTrack) {
        console.warn('[VideoFilter] No video track in stream');
        resolve(null);
        return;
      }

      const settings = videoTrack.getSettings();
      this.canvas.width = settings.width || 640;
      this.canvas.height = settings.height || 480;
      this.bgCanvas.width = this.canvas.width;
      this.bgCanvas.height = this.canvas.height;

      this.sourceVideo.srcObject = new MediaStream([videoTrack]);

      this._onVideoReady = () => {
        if (this.sourceVideo.videoWidth && this.sourceVideo.videoHeight) {
          this.canvas.width = this.sourceVideo.videoWidth;
          this.canvas.height = this.sourceVideo.videoHeight;
          this.bgCanvas.width = this.canvas.width;
          this.bgCanvas.height = this.canvas.height;
        }

        this.running = true;
        this._drawFrame();

        this.filteredStream = this.canvas.captureStream(30);
        console.log('[VideoFilter] Pipeline started, canvas:', this.canvas.width, 'x', this.canvas.height);
        resolve(this.filteredStream);
      };

      this.sourceVideo.addEventListener('loadedmetadata', this._onVideoReady, { once: true });
      this.sourceVideo.play().catch(() => {});

      if (this.sourceVideo.readyState >= 2) {
        this.sourceVideo.removeEventListener('loadedmetadata', this._onVideoReady);
        this._onVideoReady();
      }
    });
  }

  _drawFrame() {
    if (!this.running) return;

    if (this.sourceVideo.readyState >= 2) {
      const w = this.canvas.width;
      const h = this.canvas.height;

      // If background effect is active and segmentation is ready
      if (this.backgroundMode && this.segReady && this.segEngine) {
        this._drawWithBackground(w, h);
      } else {
        // Simple filter-only mode
        this.ctx.filter = this.currentFilter;
        this.ctx.drawImage(this.sourceVideo, 0, 0, w, h);
      }
    }

    this.frameCount++;
    this.rafId = requestAnimationFrame(() => this._drawFrame());
  }

  _drawWithBackground(w, h) {
    // Run segmentation every 2nd frame for performance (15fps)
    if (this.frameCount % 2 === 0) {
      const mask = this.segEngine.segment(this.sourceVideo, performance.now());
      if (mask) {
        // Extract mask pixel data
        const maskCanvas = mask.canvas || mask;
        this.bgCtx.clearRect(0, 0, w, h);
        this.bgCtx.drawImage(maskCanvas, 0, 0, w, h);
        this.lastMaskData = this.bgCtx.getImageData(0, 0, w, h);
      }
    }

    if (!this.lastMaskData) {
      // No mask yet, just draw normally
      this.ctx.filter = this.currentFilter;
      this.ctx.drawImage(this.sourceVideo, 0, 0, w, h);
      return;
    }

    // Step 1: Draw background
    this.ctx.filter = 'none';
    if (this.backgroundMode === 'blur' || this.backgroundMode === 'blur-heavy') {
      const blurAmount = this.backgroundMode === 'blur-heavy' ? 20 : 10;
      this.ctx.filter = `blur(${blurAmount}px)`;
      this.ctx.drawImage(this.sourceVideo, 0, 0, w, h);
      this.ctx.filter = 'none';
    } else if (this.backgroundMode === 'gradient' || this.backgroundMode === 'solid') {
      this._drawColorBackground(w, h);
    } else if (this.backgroundMode === 'image' && this._bgImage) {
      this.ctx.drawImage(this._bgImage, 0, 0, w, h);
    }

    // Step 2: Apply color filter to person
    this.bgCtx.filter = this.currentFilter;
    this.bgCtx.drawImage(this.sourceVideo, 0, 0, w, h);

    // Step 3: Read person pixels and mask
    const personData = this.bgCtx.getImageData(0, 0, w, h);
    const bgData = this.ctx.getImageData(0, 0, w, h);
    const maskPixels = this.lastMaskData.data;
    const personPixels = personData.data;
    const bgPixels = bgData.data;

    // Composite: where mask > 0, use person pixels; otherwise keep background
    for (let i = 0; i < maskPixels.length; i += 4) {
      const maskVal = maskPixels[i]; // Red channel of mask (0=bg, 255=person)
      if (maskVal > 128) {
        bgPixels[i] = personPixels[i];
        bgPixels[i + 1] = personPixels[i + 1];
        bgPixels[i + 2] = personPixels[i + 2];
        bgPixels[i + 3] = 255;
      }
    }

    this.ctx.putImageData(bgData, 0, 0);
  }

  _drawColorBackground(w, h) {
    if (this.backgroundMode === 'solid') {
      this.ctx.fillStyle = this.backgroundValue || '#000000';
      this.ctx.fillRect(0, 0, w, h);
    } else if (this.backgroundMode === 'gradient') {
      // Parse gradient (e.g. "linear-gradient(135deg, #1a0533, #4a1a7a, #2d1b69)")
      const colors = (this.backgroundValue || '').match(/#[0-9a-fA-F]{6}/g) || ['#1a0533', '#4a1a7a'];
      const grad = this.ctx.createLinearGradient(0, 0, w, h);
      colors.forEach((c, i) => grad.addColorStop(i / Math.max(colors.length - 1, 1), c));
      this.ctx.fillStyle = grad;
      this.ctx.fillRect(0, 0, w, h);
    }
  }

  /**
   * Change the active CSS filter.
   */
  setFilter(filterId) {
    const filterDef = VIDEO_FILTERS.find(f => f.id === filterId);
    this.currentFilter = filterDef ? filterDef.filter : 'none';
    console.log('[VideoFilter] Filter changed to:', filterId);
  }

  /**
   * Set background effect mode.
   * @param {string|null} mode - 'blur' | 'blur-heavy' | 'gradient' | 'solid' | 'image' | null
   * @param {string} value - gradient CSS, hex color, or image URL
   */
  async setBackground(mode, value) {
    console.log('[VideoFilter] Background mode:', mode, value ? '(has value)' : '');
    this.backgroundMode = mode;
    this.backgroundValue = value;
    this.lastMaskData = null;

    if (mode && !this.segReady) {
      // Initialize segmentation on first background use
      if (!this.segEngine) {
        this.segEngine = new SegmentationEngine();
      }
      const ready = await this.segEngine.init();
      this.segReady = ready;
      if (!ready) {
        console.warn('[VideoFilter] Segmentation not available, background effects disabled');
        this.backgroundMode = null;
      }
    }

    if (!mode) {
      this.segReady = false;
      this.lastMaskData = null;
    }
  }

  /**
   * Set a custom image as background.
   */
  setBackgroundImage(imageUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this._bgImage = img;
        this.setBackground('image', null);
        resolve(true);
      };
      img.onerror = () => {
        console.warn('[VideoFilter] Failed to load background image');
        resolve(false);
      };
      img.src = imageUrl;
    });
  }

  getFilteredVideoTrack() {
    if (this.filteredStream) {
      return this.filteredStream.getVideoTracks()[0] || null;
    }
    return null;
  }

  stop() {
    this.running = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.sourceVideo.srcObject) {
      this.sourceVideo.srcObject = null;
    }
    if (this.segEngine) {
      this.segEngine.destroy();
      this.segEngine = null;
    }
    this.filteredStream = null;
    this.segReady = false;
    this.lastMaskData = null;
    this._bgImage = null;
    console.log('[VideoFilter] Pipeline stopped');
  }
}
